import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import {
  Suggestion,
  Suggestions,
  SectionBasedProduct,
} from "@brandclub/common-ui";
import React, { memo, useMemo } from "react";
import { useConversationScrollDetection } from "../hooks/useConversationScrollDetection";
import { ConversationMessage, ProductPreview } from "../types/conversation";
import ConciergeWelcomeHeader from "./ConciergeWelcomeHeader";
import { ConversationSkeleton } from "./ConversationSkeleton";
import { MessageListV2 } from "./MessageListV2";
import { PromptInputConcierge } from "./PromptInputConcierge";
import { ConciergeTrendingSearches } from "./ConciergeTrendingSearches";

interface ConciergeChatAreaV2Props {
  // Config
  appName?: string;
  brandId: string | null;
  conversationId: string | null;
  isEmbedded?: boolean;

  // Conversation state
  conversationHistory: ConversationMessage[];
  productMetadataMap: Map<string, SectionBasedProduct>;
  streamingProductGroups: Map<number, string[]>;
  previewSearchQueries: string[];
  previewQueryProducts: Array<{
    query: string;
    queryIndex: number;
    products: ProductPreview[];
    totalResults: number;
  }>;
  followUpQuestions: string[];
  streamingFeaturedProduct: {
    product: SectionBasedProduct;
    question: string;
  } | null;
  streamingStoreLocator: {
    stacklineSku: string;
  } | null;

  // Streaming state
  isActivelyStreaming: boolean;
  streamingAIResponse: string;
  currentStatus: string;
  isWaitingForFollowUpQueries: boolean;

  // Loading state
  isLoadingConversationQuery: boolean;

  // Handlers
  handleSearch: (
    searchTerm: string,
    imageData?: string,
    imageMimeType?: string
  ) => void;
  onUpdateFeedback?: (
    messageIndex: number,
    feedback: { type: "like" | "dislike"; timestamp: string } | null
  ) => void;
  /** Callback when a variant is selected in a FeaturedProductWidget - triggers navigation to that variant's PDP */
  onVariantSelect?: (stacklineSku: string) => void;

  // Input state
  searchInputValue: string;
  setSearchInputValue: (value: string) => void;
}

// Separate input component that can re-render without affecting conversation area
const ConciergeInputArea: React.FC<{
  searchInputValue: string;
  setSearchInputValue: (value: string) => void;
  handleSearch: (
    text: string,
    imageData?: string,
    imageMimeType?: string
  ) => void;
  isActivelyStreaming: boolean;
  conversationHistoryLength: number;
  shouldShowSuggestions: boolean;
  showSuggestions: boolean;
  followUpQuestions: string[];
}> = ({
  searchInputValue,
  setSearchInputValue,
  handleSearch,
  isActivelyStreaming,
  conversationHistoryLength,
  shouldShowSuggestions,
  showSuggestions,
  followUpQuestions,
}) => {
  const placeholder = useMemo(
    () =>
      conversationHistoryLength > 0
        ? "Ask a follow up question"
        : "Ask anything",
    [conversationHistoryLength]
  );
  const suggestionsSection = useMemo(() => {
    if (!shouldShowSuggestions) return null;
    return (
      <div
        className={`tw:mx-auto tw:w-full tw:max-w-[768px] tw:transition-all tw:duration-300 tw:ease-out ${
          showSuggestions
            ? "tw:opacity-100 tw:translate-y-0 tw:max-h-96 tw:pointer-events-auto"
            : "tw:opacity-0 tw:translate-y-2 tw:max-h-0 tw:overflow-hidden tw:pointer-events-none"
        }`}
        style={{
          transitionDelay: showSuggestions ? "100ms" : "0ms",
        }}
      >
        <Suggestions>
          {followUpQuestions.map((question) => (
            <Suggestion
              key={question}
              suggestion={question}
              onClick={(suggestion) => setSearchInputValue(suggestion)}
            />
          ))}
        </Suggestions>
      </div>
    );
  }, [
    shouldShowSuggestions,
    showSuggestions,
    followUpQuestions,
    setSearchInputValue,
  ]);

  return (
    <div
      className="tw:flex tw:shrink-0 tw:flex-col tw:gap-4 tw:px-4 tw:pt-4 md:tw:px-0"
      style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
    >
      {suggestionsSection}
      <PromptInputConcierge
        placeholder={placeholder}
        value={searchInputValue}
        onChange={setSearchInputValue}
        onSubmit={handleSearch}
        isStreaming={isActivelyStreaming}
        expandDirection="up"
      />
    </div>
  );
};

const ConciergeChatAreaV2Component: React.FC<ConciergeChatAreaV2Props> = ({
  appName,
  brandId,
  conversationId,
  isEmbedded = false,
  conversationHistory,
  productMetadataMap,
  streamingProductGroups,
  previewSearchQueries,
  previewQueryProducts,
  followUpQuestions,
  streamingFeaturedProduct,
  streamingStoreLocator,
  isActivelyStreaming,
  streamingAIResponse,
  currentStatus,
  isWaitingForFollowUpQueries,
  isLoadingConversationQuery,
  handleSearch,
  onUpdateFeedback,
  onVariantSelect,
  searchInputValue,
  setSearchInputValue,
}) => {
  // Derived state
  const showWelcome =
    conversationHistory.length === 0 && !isLoadingConversationQuery;
  // Show typing indicator only when actively streaming but no search queries/products AND no text content yet
  const showTypingIndicator =
    isActivelyStreaming &&
    !streamingAIResponse &&
    previewSearchQueries.length === 0 &&
    previewQueryProducts.length === 0;

  // Scroll-based suggestions animation
  // Memoize the hook call to prevent re-renders when searchInputValue changes
  const scrollDetectionParams = useMemo(
    () => ({
      followUpQuestions,
      isActivelyStreaming,
      conversationHistoryLength: conversationHistory.length,
      showWelcome,
      searchInputValue: "", // Not actually used in the hook, but required by interface
    }),
    [
      followUpQuestions,
      isActivelyStreaming,
      conversationHistory.length,
      showWelcome,
    ]
  );
  const { showSuggestions, shouldShowSuggestions, bottomRef } =
    useConversationScrollDetection(scrollDetectionParams);

  // Memoize handleSearch callback to prevent welcome component re-renders
  const stableHandleSearch = useMemo(() => handleSearch, [handleSearch]);

  // Memoize welcome component props to prevent re-renders
  const welcomeProps = useMemo(
    () => ({
      onSearch: stableHandleSearch,
      brandId: brandId ? parseInt(brandId) : undefined,
      isEmbedded,
    }),
    [stableHandleSearch, brandId, isEmbedded]
  );

  // Memoize the conversation content to prevent re-renders when searchInputValue changes
  const conversationContent = useMemo(() => {
    if (isLoadingConversationQuery) {
      return <ConversationSkeleton />;
    }
    if (showWelcome) {
      return <ConciergeWelcomeHeader {...welcomeProps} />;
    }
    return (
      <MessageListV2
        conversationId={conversationId}
        messages={conversationHistory}
        showTypingIndicator={showTypingIndicator}
        streamingAIResponse={streamingAIResponse}
        productMetadataMap={productMetadataMap}
        streamingProductGroups={streamingProductGroups}
        currentStatus={currentStatus}
        previewSearchQueries={previewSearchQueries}
        previewQueryProducts={previewQueryProducts}
        isWaitingForFollowUpQueries={isWaitingForFollowUpQueries}
        isActivelyStreaming={isActivelyStreaming}
        streamingFeaturedProduct={streamingFeaturedProduct}
        streamingStoreLocator={streamingStoreLocator}
        onSearch={handleSearch}
        onUpdateFeedback={onUpdateFeedback}
        onVariantSelect={onVariantSelect}
        bottomRef={bottomRef as unknown as React.RefObject<HTMLDivElement>}
      />
    );
  }, [
    isLoadingConversationQuery,
    showWelcome,
    welcomeProps,
    conversationId,
    conversationHistory,
    showTypingIndicator,
    streamingAIResponse,
    productMetadataMap,
    streamingProductGroups,
    currentStatus,
    previewSearchQueries,
    previewQueryProducts,
    isWaitingForFollowUpQueries,
    isActivelyStreaming,
    streamingFeaturedProduct,
    streamingStoreLocator,
    handleSearch,
    onUpdateFeedback,
    onVariantSelect,
    bottomRef,
  ]);

  return (
    <div
      className="tw:relative tw:flex tw:flex-1 tw:flex-col"
      style={{ minHeight: 0 }}
    >
      {/* Disable smooth resize on welcome screen to prevent flickering */}
      <div
        style={{
          flex: 1,
          overflow: showWelcome ? "hidden" : "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {showWelcome ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              overflow: "auto",
            }}
          >
            <div style={{ height: "120px", flexShrink: 0 }} />
            {conversationContent}
            <div className="tw:mt-8 tw:w-full tw:max-w-[768px] tw:px-4 md:tw:px-0">
              <PromptInputConcierge
                placeholder="Ask anything"
                value={searchInputValue}
                onChange={setSearchInputValue}
                onSubmit={handleSearch}
                isStreaming={isActivelyStreaming}
                expandDirection="down"
                maxHeight={200}
              />
            </div>
            <ConciergeTrendingSearches onSearch={handleSearch} />
            <div style={{ height: "40px", flexShrink: 0 }} />
          </div>
        ) : (
          <Conversation>
            <ConversationContent className="tw:flex tw:items-center tw:justify-center">
              {conversationContent}
            </ConversationContent>
            <ConversationScrollButton />
          </Conversation>
        )}
      </div>

      {/* Input at bottom for conversation view only */}
      {!showWelcome && (
        <ConciergeInputArea
          searchInputValue={searchInputValue}
          setSearchInputValue={setSearchInputValue}
          handleSearch={handleSearch}
          isActivelyStreaming={isActivelyStreaming}
          conversationHistoryLength={conversationHistory.length}
          shouldShowSuggestions={shouldShowSuggestions}
          showSuggestions={showSuggestions}
          followUpQuestions={followUpQuestions}
        />
      )}
    </div>
  );
};

// Memoize the chat area - input is separated so it can re-render without affecting conversation
export const ConciergeChatAreaV2 = memo(
  ConciergeChatAreaV2Component
  // (prevProps, nextProps) => {
  //   // Skip re-render if only searchInputValue changed
  //   return (
  //     prevProps.conversationHistory === nextProps.conversationHistory &&
  //     prevProps.isActivelyStreaming === nextProps.isActivelyStreaming &&
  //     prevProps.streamingAIResponse === nextProps.streamingAIResponse &&
  //     prevProps.isLoadingConversationQuery ===
  //       nextProps.isLoadingConversationQuery &&
  //     prevProps.followUpQuestions === nextProps.followUpQuestions &&
  //     prevProps.previewSearchQueries === nextProps.previewSearchQueries &&
  //     prevProps.previewQueryProducts === nextProps.previewQueryProducts &&
  //     prevProps.streamingFeaturedProduct ===
  //       nextProps.streamingFeaturedProduct &&
  //     prevProps.productMetadataMap === nextProps.productMetadataMap &&
  //     prevProps.streamingProductGroups === nextProps.streamingProductGroups &&
  //     prevProps.currentStatus === nextProps.currentStatus &&
  //     prevProps.isWaitingForFollowUpQueries ===
  //       nextProps.isWaitingForFollowUpQueries &&
  //     prevProps.conversationId === nextProps.conversationId &&
  //     prevProps.brandId === nextProps.brandId &&
  //     prevProps.isEmbedded === nextProps.isEmbedded &&
  //     prevProps.appName === nextProps.appName &&
  //     prevProps.handleSearch === nextProps.handleSearch &&
  //     prevProps.onRetry === nextProps.onRetry &&
  //     prevProps.onUpdateFeedback === nextProps.onUpdateFeedback &&
  //     prevProps.setSearchInputValue === nextProps.setSearchInputValue
  //     // Intentionally NOT comparing searchInputValue - input is in separate component
  //   );
  // }
);
